import React, { useState, useEffect } from "react"
import { useRowEditor }    from "../context/QuoteEditorContext"
import { useRowSelection } from "../context/TreeViewContext"
import { calcComponentTotal } from "../hooks/useCalculations"
import { formatNumber, roundNumber } from "../helpers/formatting"
import Input            from "@react/shared/ui/Input"
import Badge            from "@react/shared/ui/Badge"
import DragHandle       from "@react/shared/ui/DragHandle"
import IconBadge        from "@react/shared/ui/IconBadge"
import IconButton       from "@react/shared/ui/IconButton"
import { TreeLead, TreeStem } from "@react/shared/ui/TreeGuides"
import RowMenu          from "./RowMenu"
import DecimalInput     from "./DecimalInput"
import RowCheckbox      from "./RowCheckbox"
import KindPicker       from "./KindPicker"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { findCostCategory, OUVRAGE_VISUAL } from "@react/quotes/qline_service_blueprints/kinds"
import { policyPriceFor, isPolicyPriced } from "../helpers/coefficients"
import { hasLiveChildren } from "../helpers/treeMath"
import { RichTextEditor, RichTextView, isBlank, toPlainText } from "./RichText"

const marginTone = pct => (pct >= 25 ? "text-emerald-600" : pct >= 15 ? "text-amber-600" : "text-rose-600")

const deriveCoef = (pv, pa) => (pa > 0 ? pv / pa : 0)
const parseNum   = v => {
  const n = parseFloat(String(v).replace(",", "."))
  return Number.isFinite(n) ? n : 0
}

const CELL_END    = "flex items-center min-w-0 row-start-1 justify-end"
const CELL_CENTER = "flex items-center min-w-0 row-start-1 justify-center"
const STATIC_NUM  = "py-1 tabular-nums text-slate-500 text-[12.5px] whitespace-nowrap"

const labelTone = (composed, nesting) =>
  composed    ? "font-semibold text-slate-900" :
  nesting > 0 ? "text-slate-700"               :
                "font-medium text-slate-900"

const ComponentBlock = ({ node, parentQty = 1, sectionUid = null, depth = 0, nesting = 0, trail = [], last = false }) => {
  const { settings, dispatch, getState } = useRowEditor()
  const selection = useRowSelection(node._uid)

  const composed = hasLiveChildren(node)
  const open     = composed && node._expanded !== false

  const qty   = parseFloat(node.quantity) || 0
  const pa    = parseFloat(node.purchase_price) || 0
  const pv    = parseFloat(node.unit_price) || 0
  // A line sold at 0 shows 0, not its stored coefficient.
  const coef  = deriveCoef(pv, pa)
  const total = calcComponentTotal(node, "sale", parentQty)
  const margin = pv > 0 ? ((pv - pa) / pv) * 100 : 0

  const patch = fields => dispatch({ type: "UPDATE_NODE", payload: { nodeUid: node._uid, fields } })

  const categoryLabel = findCostCategory(node.cost_category)?.label || "—"
  const placeholder   = composed ? I18n.t("quotes.builder.component.composed_label") : categoryLabel
  const hasDescription = !isBlank(node.description)

  const toggle = (
    <IconButton
      size       = "xs"
      data-twist = ""
      onClick    = {() => dispatch({ type: "TOGGLE_NODE", payload: { nodeUid: node._uid } })}
      title      = {I18n.t("quotes.builder.aggregate.toggle_collapse")}
      className  = "shrink-0 [&_svg]:transition-transform [&_svg]:text-[10px]"
    >
      <FontAwesomeIcon icon="chevron-down" />
    </IconButton>
  )
  const [editingDesc, setEditingDesc] = useState(!!node._focusDescription)

  // Cleared so that a re-render does not reopen the input.
  useEffect(() => {
    if (node._focusDescription) {
      patch({ _focusDescription: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // Kept raw while typing: a quantity of 0 is legitimate, an optional variant.
  const onChangeQty  = v => patch({ quantity: v })
  const onChangeUnit = v => patch({ unit: v })
  // Correcting a purchase price recomputes the selling price only when that
  // price came from the policy: a negotiated price never moves on its own.
  const onChangePA   = v => {
    const newPA = parseNum(v)
    const state = getState()
    const suit  = pv <= 0 || isPolicyPriced(state, node)

    if (!suit) { patch({ purchase_price: newPA }); return }

    const fromPolicy = policyPriceFor(state, node.cost_category || "materials", newPA)
    if (fromPolicy != null) {
      patch({ purchase_price: newPA, unit_price: roundNumber(fromPolicy), _pricedByPolicy: true })
      return
    }

    patch({ purchase_price: newPA })
  }

  // Typing a price or a coefficient takes the line out of the policy for good.
  const onChangePV   = v => patch({ unit_price: parseNum(v), _pricedByPolicy: false })
  const onChangeCoef = v => {
    const newCoef = parseNum(v)
    if (pa <= 0) patch({ coefficient: newCoef, _pricedByPolicy: false })
    else         patch({ coefficient: newCoef, unit_price: roundNumber(pa * newCoef), _pricedByPolicy: false })
  }

  return (
    <>
    <div
      data-uid       = {node._uid}
      onFocusCapture = {() => sectionUid && dispatch({ type: "SET_ACTIVE_SECTION", payload: { sectionUid } })}
      className      = "qb-grid qb-row-line items-center px-3 py-1 bg-white relative group hover:bg-slate-50"
    >
      {/* Over the first two columns: a deep tree pushes its label along instead of running over it. */}
      <div className="qb-tree col-start-1 col-end-3 row-start-1 flex items-stretch self-stretch min-w-0">
        <div className="flex items-center gap-1 shrink-0">
          <RowCheckbox
            checked   = {selection.checked}
            onToggle  = {selection.onToggle}
            label     = {I18n.t("quotes.builder.selection.check_row")}
          />
          <DragHandle
            reveal    = "onHover"
            size      = "sm"
            icon      = "bars"
            className = "!opacity-0 group-hover:!opacity-100 shrink-0"
          />
        </div>

        <div className="flex items-stretch shrink-0 ml-1">
          <TreeLead depth={depth} trail={trail} last={last} toggle={composed ? toggle : null} />
          <span className="relative shrink-0 flex items-center">
            {composed ? (
              <IconBadge
                tone  = {OUVRAGE_VISUAL.tone}
                size  = "sm"
                title = {`${I18n.t("quotes.builder.component.composed_label")} · ${I18n.t("quotes.builder.component.composed_price_hint")}`}
              >
                <FontAwesomeIcon icon={OUVRAGE_VISUAL.icon} />
              </IconBadge>
            ) : (
              <KindPicker
                value             = {node.cost_category}
                enabledCategories = {settings.account?.enabled_cost_categories}
                onChange          = {next => patch({ cost_category: next, ...(next === "labor" ? { unit: "h" } : {}) })}
              />
            )}
            {open && <TreeStem />}
          </span>
        </div>

        <div className="qb-tree-label flex items-center gap-2 min-w-0 flex-1 pl-5">
          {node.ref_product_distrib && (
            <Badge tone="neutral" variant="soft" size="xs" className="font-mono tracking-[0.02em]" title={node.ref_product_distrib}>
              {node.ref_product_distrib}
            </Badge>
          )}
          {editingDesc ? (
            <RichTextEditor
              value       = {node.description}
              placeholder = {placeholder}
              onChange    = {html => patch({ description: html })}
              onDone      = {() => setEditingDesc(false)}
              onCancel    = {() => setEditingDesc(false)}
              // Enter validates, as in a spreadsheet; Shift+Enter breaks the line.
              onEnter     = {() => {
                setEditingDesc(false)
                dispatch({ type: "ADD_SIBLING_LINE", payload: { nodeUid: node._uid } })
              }}
              className   = {`flex-1 min-w-0 text-[13px] leading-[1.35] ${labelTone(composed, nesting)}`}
            />
          ) : hasDescription ? (
            <RichTextView
              value     = {node.description}
              onClick   = {() => setEditingDesc(true)}
              // The whole label on hover: in compact mode three lines are never
              // enough, and the text must stay reachable without editing.
              title     = {toPlainText(node.description)}
              className = {`qb-desc flex-1 min-w-0 cursor-text text-[13px] leading-[1.35] ${labelTone(composed, nesting)}`}
            />
          ) : (
            <span
              onClick   = {() => setEditingDesc(true)}
              title     = {I18n.t("quotes.builder.aggregate.click_to_edit_title")}
              className = "qb-desc flex-1 min-w-0 cursor-text text-[13px] leading-[1.35] font-medium text-slate-500"
            >
              {placeholder}
            </span>
          )}
        </div>
      </div>

      <div className={`${CELL_END} col-start-3`}>
        <Input
          variant   = "inline"
          align     = "right"
          type      = "number"
          step      = "0.01"
          min       = "0"
          value     = {node.quantity ?? ""}
          onChange  = {e => onChangeQty(e.target.value)}
          title     = {qty > 0 ? undefined : I18n.t("quotes.builder.component.zero_quantity_hint")}
          className = {`h-7 text-[12.5px] ${qty > 0 ? "" : "text-slate-400"}`}
        />
      </div>
      <div className={`${CELL_CENTER} col-start-4`}>
        <Input
          variant     = "inline"
          align       = "center"
          type        = "text"
          value       = {node.unit || ""}
          onChange    = {e => onChangeUnit(e.target.value)}
          disabled    = {!composed && node.cost_category === "labor"}
          title       = {!composed && node.cost_category === "labor" ? I18n.t("quotes.builder.component.labor_unit_locked") : undefined}
          placeholder = {I18n.t("quotes.builder.component.unit_placeholder")}
          className   = "h-7 text-[12.5px]"
        />
      </div>

      {composed ? (
        <>
          <div className={`${CELL_END} qb-col-purchase col-start-5`} title={I18n.t("quotes.builder.component.composed_price_hint")}>
            <span className={STATIC_NUM}>{pa ? formatNumber(pa) : "—"}</span>
          </div>
          <div className={`${CELL_END} qb-col-coef col-start-6`}>
            <span className={STATIC_NUM}>{pa > 0 ? formatNumber(coef) : "—"}</span>
          </div>
          <div className={`${CELL_END} col-start-7`} title={I18n.t("quotes.builder.component.composed_price_hint")}>
            <span className={STATIC_NUM}>{formatNumber(pv)}</span>
          </div>
        </>
      ) : (
        <>
          <div className={`${CELL_END} qb-col-purchase col-start-5`}>
            <DecimalInput
              variant  = "inline"
              align    = "right"
              value    = {pa || ""}
              disabled = {settings.readOnlyPurchasePrice}
              onChange = {onChangePA}
              className = "h-7 text-[12.5px]"
            />
          </div>

          <div className={`${CELL_END} qb-col-coef col-start-6`}>
            <DecimalInput
              variant  = "inline"
              align    = "right"
              value    = {pa > 0 ? Math.round(coef * 100) / 100 : ""}
              disabled = {pa <= 0}
              onChange = {onChangeCoef}
              className = "h-7 text-[12.5px]"
            />
          </div>

          <div className={`${CELL_END} col-start-7`}>
            <DecimalInput
              variant  = "inline"
              align    = "right"
              value    = {pv || ""}
              onChange = {onChangePV}
              className = "h-7 text-[12.5px]"
            />
          </div>
        </>
      )}

      <div className={`${CELL_END} qb-col-margin col-start-8`}>
        <span className={`${STATIC_NUM} font-semibold ${pa && pv > 0 ? marginTone(margin) : ""}`}>
          {pa && pv > 0 ? `${margin >= 0 ? "+" : ""}${margin.toFixed(1)} %` : "—"}
        </span>
      </div>

      <div className={`${CELL_CENTER} col-start-9`} aria-hidden="true" />

      <div className={`${CELL_END} col-start-10`}>
        <span className="py-1 tabular-nums text-slate-900 font-semibold text-[13px] -tracking-[0.005em] whitespace-nowrap">
          {formatNumber(total)} {settings.currencySymbol}
        </span>
      </div>

      <div className="col-start-11 row-start-1 flex items-center justify-end">
        <RowMenu node={node} />
      </div>
    </div>
    </>
  )
}

export default ComponentBlock
